import { useState, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FAQItem } from '../../../../components/ui-components/FAQItem'
import { PrimaryButton } from '../../../../components/execution/PrimaryButton'
import { useBottomSheet } from '../../../../state-management/contexts/BottomSheetContext'
import { convertEpochToDate } from '../../../../utils/utils'
import { openVideoPlayer, postCTA, trackEvent } from '../../../../utils/appBridge'
import { getEliteCTA } from '../../../../utils/mutualFundDetail'
import { getBottomPadding } from '../../../../utils/sessionStorage'
import { EventNames } from '../../../../types/misc.types'
import { taxHarvestingFaqData, taxHarvestingFaqDataTemp } from '../../data/taxHarvestingFaqData'
import ListItem, { ListItemGroup } from './ListItem'
import ImportantNoteBottomSheet from '../../bottom-sheets/ImportantNoteBottomSheet'
import TaxationRulesBottomSheet from '../../bottom-sheets/TaxationRulesBottomSheet'
import ImportantNoteIconSmall from './ImportantNoteIconSmall'
import TaxationRulesIconSmall from './TaxationRulesIconSmall'
import { TAXATION_RULES_HEADING_ID } from '../../bottom-sheets/taxationRulesConstants'
import { fetchTaxHarvestingSummary } from '../../api/taxHarvestingSummary'
import type { TaxHarvestingSummaryResponse } from '../../types/summary.types'
import {
  TaxHarvestingScreenName,
  TaxHarvestingEventId,
  TaxHarvestingButtonLabel,
  trackTaxHarvestingUserClicked,
  trackTaxHarvestingButtonTap
} from '../../tracking/taxHarvestingTracking'
import { useAppMetaContent } from '../../../../state-management/providers/AppMetaContentProvider'

type AccordionState = Record<string, boolean>

type TaxHarvestingFooterProps = {
  showFloatingButtonIfUserIsNotElite?: boolean
  compactForNonEliteSummary?: boolean
}

export default function TaxHarvestingFooter({
  showFloatingButtonIfUserIsNotElite = false,
  compactForNonEliteSummary = false
}: TaxHarvestingFooterProps) {
  const [accordionState, setAccordionState] = useState<AccordionState>({})
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const { getEliteMembersDisplay } = useAppMetaContent()
  const taxationRulesButtonRef = useRef<HTMLButtonElement>(null)

  const { data } = useQuery<TaxHarvestingSummaryResponse>({
    queryKey: ['taxHarvestingSummary'],
    queryFn: fetchTaxHarvestingSummary
  })

  const lastPortfolioFetchDate = data?.lastFetchedTimestamp
    ? convertEpochToDate(data.lastFetchedTimestamp)
    : ''
  const isUserElite = data?.isUserElite ?? false

  const handleImportantNoteClick = () => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.Footer,
      eventId: TaxHarvestingEventId.ImportantNote
    })
    openBottomSheet(<ImportantNoteBottomSheet onDismiss={closeBottomSheet} />, closeBottomSheet)
  }

  const closeTaxationRulesSheet = () => {
    closeBottomSheet()
    // TR_B08 — restore VoiceOver focus to Taxation rules row after dismissing the sheet
    window.setTimeout(() => taxationRulesButtonRef.current?.focus(), 400)
  }

  const handleTaxationRulesClick = () => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.Footer,
      eventId: TaxHarvestingEventId.TaxationRules
    })
    openBottomSheet(
      <TaxationRulesBottomSheet onDismiss={closeTaxationRulesSheet} />,
      closeTaxationRulesSheet,
      { drawerTitleId: TAXATION_RULES_HEADING_ID }
    )
  }

  const handleEliteCtaClick = () => {
    trackTaxHarvestingButtonTap({
      screen: TaxHarvestingScreenName.Footer,
      eventId: TaxHarvestingEventId.UnlockEliteCta,
      buttonLabel: TaxHarvestingButtonLabel.UnlockPlanWithElite
    })
    postCTA(JSON.stringify(getEliteCTA()))
  }

  const handleFaqAccordionClick = (title: string) => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.Footer,
      eventId: TaxHarvestingEventId.FaqAccordion,
      buttonLabel: title
    })
    setAccordionState((prev) => ({
      ...prev,
      [title]: !prev?.[title]
    }))
  }

  const shouldShowFloatingButton = !isUserElite && showFloatingButtonIfUserIsNotElite && !compactForNonEliteSummary

  const videoId = '1cc55c35c7f841659105cfc1cb9a723c'
  const moduleId = 'MD1bc7004b978349ed8da875e590e6db2d'
  const courseId = 'COURSEde919cce87264b1480e91195a96442b0'
  const videoUrl = `https://pumobileweb.uni.club/power-academy/video-player?videoId=${videoId}&moduleId=${moduleId}&courseId=${courseId}&prevStatusBarColor=20A5DE`

  return (
    <>
      <div
        className='w-full bg-[#F0F4F7] flex flex-col pt-8 px-5'
        style={{ paddingBottom: `calc(${getBottomPadding()}px + ${shouldShowFloatingButton ? '164px' : '48px'})` }}
      >
        {!compactForNonEliteSummary ? (
          <div
            role='button'
            tabIndex={0}
            onClick={() => {
              trackEvent({
                eventName: EventNames.ButtonTap,
                payload: {
                  screenType: TaxHarvestingScreenName.Footer,
                  buttonLabel: 'Open Video Player',
                  eventId: 'tax_harvesting_video'
                }
              })
              openVideoPlayer(videoUrl)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                openVideoPlayer(videoUrl)
              }
            }}
            aria-label='Know what you get in your personalised Tax-Saving Plans, by Arush Kapoor, Head of Product. Video'
            aria-description='Double tap to play video'
            className='relative mb-6 cursor-pointer'
          >
            <svg
              className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
              xmlns='http://www.w3.org/2000/svg'
              width='40'
              height='40'
              viewBox='0 0 40 40'
              fill='none'
              aria-hidden='true'
            >
              <circle cx='20' cy='20' r='20' fill='#D6DCE1' />
              <path
                d='M17 26.9991V14.0723C17 13.2353 17.967 12.7686 18.6222 13.2895L26.9866 19.9381C27.4992 20.3456 27.4884 21.1276 26.9647 21.5207L18.6003 27.7988C17.9411 28.2936 17 27.8233 17 26.9991Z'
                fill='white'
              />
            </svg>
            <img className='rounded-xl' src='/images/tax-harvesting.png' alt='' />
          </div>
        ) : null}
        {lastPortfolioFetchDate ? (
          <div className='text-[14px] font-[400] text-[#6A6E6E] pb-6'>
            {compactForNonEliteSummary
              ? `As per portfolio on ${lastPortfolioFetchDate}`
              : `As per last portfolio fetch on ${lastPortfolioFetchDate}`}
            <br />
          </div>
        ) : null}
        <div className='text-[14px] font-[400] text-[#6A6E6E] pb-6'>
          This summary is for mutual funds only. Gains and losses from <span className='font-medium'>stocks, ETFs etc are not included.</span>
        </div>
        {!compactForNonEliteSummary ? (
          <div className='text-[14px] font-[400] text-[#6A6E6E] pb-6'>
            Exit load is considered while giving you a plan. Tax saving will always be greater than exit load charged on withdrawals.
          </div>
        ) : null}
        <div className='bg-white rounded-xl mb-4'>
          <ListItemGroup>
            <ListItem
              icon={<ImportantNoteIconSmall />}
              label='Important note'
              onClick={handleImportantNoteClick}
            />
            {!compactForNonEliteSummary ? (
              <ListItem
                ref={taxationRulesButtonRef}
                icon={<TaxationRulesIconSmall />}
                label='Taxation rules'
                onClick={handleTaxationRulesClick}
              />
            ) : null}
          </ListItemGroup>
        </div>
        {!compactForNonEliteSummary ? (
          <div className='flex flex-col gap-[1px] overflow-hidden mt-8'>
            <h2 className='text-[#6A6E6E] text-[13px] pb-2 pl-4 font-[400]'>FAQs</h2>
            {/* Tax summary_B14: explicit list roles — list-style:none drops list semantics in TalkBack/WebKit */}
            <ul role='list' className='rounded-[12px] flex flex-col gap-[1px] overflow-hidden bg-white list-none p-0 m-0'>
              {taxHarvestingFaqDataTemp.map((faq, index) => {
                const isLastItem = index === taxHarvestingFaqData.length - 1

                return (
                  <li
                    key={faq.title}
                    role='listitem'
                    className={isLastItem ? '' : 'border-b border-[#EBF2F2]'}
                  >
                    <FAQItem
                      titleClassName='!text-[13px] font-[500]'
                      title={faq.title}
                      description={faq.descp}
                      isExpanded={!!accordionState?.[faq.title]}
                      onAccordionClick={() => handleFaqAccordionClick(faq.title)}
                    />
                  </li>
                )
              })}
            </ul>
          </div>
        ) : null}

        {shouldShowFloatingButton && (
          <div style={{ paddingBottom: `calc(${getBottomPadding()}px + 8px)` }}
            className='pt-5 flex flex-col items-center gap-2 px-5 
            absolute bottom-0 left-0 right-0 h-max bg-white z-[1000] border-t border-[#F0F4F7]'>
            <PrimaryButton
              onClick={handleEliteCtaClick}
              label='Unlock plan with Elite'
              primaryButtonClassName='text-[16px] !h-[56px]' />
            <p className='text-[13px] font-[400] text-[#232323] text-center'>
              Trusted by {getEliteMembersDisplay('short')} Elite members
            </p>
          </div>
        )}
      </div>
    </>
  )
}

